import React, { useEffect, useMemo, useState } from "react";
import { useAsyncData } from "../hooks/useAsyncData";
import { API_URL } from "../config/config";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useApi } from "../api/useApi";
import { IJobView } from "../interfaces/job/iJobView.interface";
import {
  sectionTitleClasses,
  gridClasses,
  primaryBtnClasses,
  secondaryBtnClasses,
} from "../styles/uiClasses";
import { PageContainer } from "../components/PageContainer";
import { FilterBar } from "../components/FilterBar";
import { SkeletonGrid } from "../components/SkeletonGrid";
import { EmptyState } from "../components/EmptyState";
import { JobCard } from "../components/JobCard";
import { PlusIcon, PencilIcon } from "./../icons/SvgIcons";
import { JobSortingEnum } from "../enums/jobSorting.enum";
import { userProfilePageClasses } from "../styles/pageSpecificClasses";
import { ADMIN_LOGIN_PATH } from "../constants/routes";

export default function UserProfile() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { request } = useApi();

  const [q, setQ] = useState("");
  const [sort, setSort] = useState<JobSortingEnum>(JobSortingEnum.New);

  /*
  Previous user-account state, kept for later restoration:
  type TabKey = "saved" | "posted";
  const [params, setParams] = useSearchParams();
  const tabParam = (params.get("tab") as TabKey) || "saved";
  const [activeTab, setActiveTab] = useState<TabKey>(tabParam);
  */

  useEffect(() => {
    if (!token) navigate(ADMIN_LOGIN_PATH);
  }, [token, navigate]);

  const { data: postedData, loading: loadingPosted, error: postedError } =
    useAsyncData<IJobView[]>(
      async () => {
        if (!token) return [];
        const res = await request(`${API_URL}/job/GetAllByUserId`, { method: "GET" });
        const d = await res.json();
        return Array.isArray(d) ? d : [];
      },
      [token]
    );

  /*
  Previous saved-jobs fetch:
  const { data: savedData, loading: loadingSaved, error: savedError } =
    useAsyncData<IJobView[]>(
      async () => {
        if (!token) return [];
        const res = await request(`${API_URL}/userSavedJob/GetAllByUserId`, { method: "GET" });
        const d = await res.json();
        return Array.isArray(d) ? d : [];
      },
      [token]
    );
  */

  const postedJobs = postedData ?? [];
  const error = postedError;
  const list = postedJobs;
  const loading = loadingPosted;

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    let arr = list.filter(
      (j) =>
        !ql ||
        j.title?.toLowerCase().includes(ql) ||
        j.jobCategoryName?.toLowerCase().includes(ql) ||
        j.locationName?.toLowerCase().includes(ql) ||
        j.locationRegionName?.toLowerCase().includes(ql)
    );

    arr = arr.sort((a, b) => {
      const da = a.effectiveFrom ? +new Date(a.effectiveFrom) : 0;
      const db = b.effectiveFrom ? +new Date(b.effectiveFrom) : 0;
      if (sort === JobSortingEnum.New) return db - da;
      if (sort === JobSortingEnum.Old) return da - db;
      if (sort === JobSortingEnum.SalaryDesc) {
        return (a.salary ?? 0) < (b.salary ?? 0) ? 1 : -1;
      }
      if (sort === JobSortingEnum.SalaryAsc) {
        return (a.salary ?? 0) > (b.salary ?? 0) ? 1 : -1;
      }
      return 0;
    });

    return arr;
  }, [list, q, sort]);

  return (
    <PageContainer maxWidth="7xl" contentClassName={userProfilePageClasses.shellSpacing}>
      <header className={userProfilePageClasses.header}>
        <div>
          <h1 className={userProfilePageClasses.title}>Admin Jobs</h1>
          <p className={userProfilePageClasses.intro}>
            Create new listings and update the roles already published on the site.
          </p>
        </div>
        <Link
          to="/createJob"
          className={`${primaryBtnClasses} ${userProfilePageClasses.primaryAction}`}
        >
          <PlusIcon className={userProfilePageClasses.primaryActionIcon} /> Post a Job
        </Link>
        <Link
          to="/admin/blog"
          className={secondaryBtnClasses}
        >
          Manage Blog
        </Link>
      </header>

      <FilterBar q={q} onQ={setQ} sort={sort} onSort={setSort} />

      {error && <div className={userProfilePageClasses.error}>{error}</div>}

      {loading ? (
        <SkeletonGrid />
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No posted jobs yet"
          subtitle="Post your first role to start building the public jobs board."
        />
      ) : (
        <section>
          {/*
          Previous tabbed heading:
          <h2 className={`${sectionTitleClasses} ${userProfilePageClasses.sectionTitle}`}>
            {activeTab === "saved" ? "Saved Jobs" : "My Posted Jobs"}
          </h2>
          */}
          <h2 className={`${sectionTitleClasses} ${userProfilePageClasses.sectionTitle}`}>
            My Posted Jobs
          </h2>
          <div className={`${gridClasses} ${userProfilePageClasses.grid}`}>
            {filtered.map((job) => (
              <JobCard
                key={job.id}
                job={job}
                to={`/job/${job.id}`}
                isSaved={false}
                primaryLabel="View Details"
                secondaryAction={
                  <button
                    onClick={() => navigate(`/editJob/${job.id}`)}
                    className={`${secondaryBtnClasses} ${userProfilePageClasses.secondaryActionPosted}`}
                  >
                    <PencilIcon className={userProfilePageClasses.secondaryActionIcon} /> Edit
                  </button>
                }
              />
            ))}
          </div>
        </section>
      )}
    </PageContainer>
  );
}
